/**
 * Toast Notification Component
 * Phase 4: Success/Error feedback system
 */

import React, { useEffect } from 'react';
import '../styles/Toast.css';
import { CheckIcon, CloseIcon, WarningIcon, InfoIcon } from '@atlas-forms/ui-components-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  /**
   * Toast message
   */
  message: string;

  /**
   * Toast type
   */
  type: ToastType;

  /**
   * Whether toast is visible
   */
  isVisible: boolean;

  /**
   * Duration in milliseconds
   * @default 3000
   */
  duration?: number;

  /**
   * Callback when toast auto-dismisses
   */
  onDismiss?: () => void;

  /**
   * Optional action button
   */
  action?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * Toast Notification - Success/Error feedback
 */
export const Toast: React.FC<ToastProps> = ({
  message,
  type,
  isVisible,
  duration = 3000,
  onDismiss,
  action,
}) => {
  // Auto-dismiss after duration
  useEffect(() => {
    if (isVisible && onDismiss && duration > 0) {
      const timer = setTimeout(() => {
        onDismiss();
      }, duration);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isVisible, duration, onDismiss]);

  if (!isVisible) {
    return null;
  }

  const getIcon = () => {
    const iconProps = { width: 16, height: 16, style: { flexShrink: 0 } };
    switch (type) {
      case 'success':
        return <CheckIcon {...iconProps} />;
      case 'error':
        return <CloseIcon {...iconProps} />;
      case 'warning':
        return <WarningIcon {...iconProps} />;
      case 'info':
        return <InfoIcon {...iconProps} />;
      default:
        return <InfoIcon {...iconProps} />;
    }
  };

  return (
    <div
      className={`af-toast af-toast--${type} ${isVisible ? 'af-toast--visible' : ''}`}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <div className="af-toast__content">
        <span className="af-toast__icon">{getIcon()}</span>
        <span className="af-toast__message">{message}</span>
      </div>

      {action && (
        <button
          type="button"
          className="af-toast__action"
          onClick={action.onClick}
        >
          {action.label}
        </button>
      )}

      {onDismiss && (
        <button
          type="button"
          className="af-toast__close"
          onClick={onDismiss}
          aria-label="Close notification"
        >
          <CloseIcon width={14} height={14} />
        </button>
      )}
    </div>
  );
};

export default Toast;
